/**
 * Per-state self-storage law reference for marketing surface.
 *
 * Mirrors services/nestplot-api/src/nestplot_api/data/state_rules.py. When
 * the backend table updates, regenerate this file:
 *
 *   cd services/nestplot-api && uv run python -c "from src.nestplot_api.data.state_rules import STATE_RULES; ..."
 *
 * (The /states/* SEO pages render from this; the operator app reads
 * the authoritative copy from the API.)
 */

export interface StateRuleMarketing {
  code: string;
  name: string;
  slug: string;
  statute: string;
  lateFeeCapCents: number;
  gracePeriodDays: number;
  noticeOfDefaultDays: number;
  lienSaleNoticeDays: number;
  moveOutNoticeDays: number;
}

export const STATE_RULES_MARKETING: StateRuleMarketing[] = [
  {
    code: "AL",
    name: "Alabama",
    slug: "alabama",
    statute: "Ala. Code §8-15-30 et seq. — Self-Service Storage Facility Act",
    lateFeeCapCents: 2000,
    gracePeriodDays: 5,
    noticeOfDefaultDays: 30,
    lienSaleNoticeDays: 15,
    moveOutNoticeDays: 14,
  },
  {
    code: "AK",
    name: "Alaska",
    slug: "alaska",
    statute: "AS §34.35.220 — Storage of Personal Property",
    lateFeeCapCents: 2000,
    gracePeriodDays: 5,
    noticeOfDefaultDays: 30,
    lienSaleNoticeDays: 14,
    moveOutNoticeDays: 14,
  },
  {
    code: "AZ",
    name: "Arizona",
    slug: "arizona",
    statute: "A.R.S. §33-1701 et seq. — Self-Service Storage Facilities Act",
    lateFeeCapCents: 2000,
    gracePeriodDays: 5,
    noticeOfDefaultDays: 10,
    lienSaleNoticeDays: 14,
    moveOutNoticeDays: 14,
  },
  {
    code: "AR",
    name: "Arkansas",
    slug: "arkansas",
    statute: "Ark. Code §18-16-401 et seq. — Self-Service Storage Facility Act",
    lateFeeCapCents: 2000,
    gracePeriodDays: 5,
    noticeOfDefaultDays: 30,
    lienSaleNoticeDays: 20,
    moveOutNoticeDays: 14,
  },
  {
    code: "CA",
    name: "California",
    slug: "california",
    statute: "CA Bus. & Prof. Code §21700-21716 — California Self-Service Storage Facility Act",
    lateFeeCapCents: 2000,
    gracePeriodDays: 5,
    noticeOfDefaultDays: 30,
    lienSaleNoticeDays: 14,
    moveOutNoticeDays: 14,
  },
  {
    code: "CO",
    name: "Colorado",
    slug: "colorado",
    statute: "C.R.S. §38-21.5-101 et seq. — Self-Service Storage Facility Liens",
    lateFeeCapCents: 2000,
    gracePeriodDays: 5,
    noticeOfDefaultDays: 15,
    lienSaleNoticeDays: 15,
    moveOutNoticeDays: 14,
  },
  {
    code: "CT",
    name: "Connecticut",
    slug: "connecticut",
    statute: "Conn. Gen. Stat. §42-160 et seq. — Self-Service Storage Facility Act",
    lateFeeCapCents: 2000,
    gracePeriodDays: 5,
    noticeOfDefaultDays: 30,
    lienSaleNoticeDays: 15,
    moveOutNoticeDays: 14,
  },
  {
    code: "DE",
    name: "Delaware",
    slug: "delaware",
    statute: "25 Del. Code §4901 et seq. — Self-Service Storage Facilities Act",
    lateFeeCapCents: 2000,
    gracePeriodDays: 5,
    noticeOfDefaultDays: 30,
    lienSaleNoticeDays: 10,
    moveOutNoticeDays: 14,
  },
  {
    code: "DC",
    name: "District of Columbia",
    slug: "district-of-columbia",
    statute: "D.C. Code §40-453 — Self-Service Storage Facility Act",
    lateFeeCapCents: 2000,
    gracePeriodDays: 5,
    noticeOfDefaultDays: 30,
    lienSaleNoticeDays: 14,
    moveOutNoticeDays: 14,
  },
  {
    code: "FL",
    name: "Florida",
    slug: "florida",
    statute: "Fla. Stat. §83.801-83.809 — Self-Service Storage Space Act",
    lateFeeCapCents: 2000,
    gracePeriodDays: 5,
    noticeOfDefaultDays: 30,
    lienSaleNoticeDays: 14,
    moveOutNoticeDays: 14,
  },
  {
    code: "GA",
    name: "Georgia",
    slug: "georgia",
    statute: "O.C.G.A. §10-4-210 et seq. — Self-Service Storage Facility Act",
    lateFeeCapCents: 2000,
    gracePeriodDays: 5,
    noticeOfDefaultDays: 10,
    lienSaleNoticeDays: 10,
    moveOutNoticeDays: 14,
  },
  {
    code: "HI",
    name: "Hawaii",
    slug: "hawaii",
    statute: "HRS §507D-1 et seq. — Self-Service Storage Facilities",
    lateFeeCapCents: 2000,
    gracePeriodDays: 5,
    noticeOfDefaultDays: 60,
    lienSaleNoticeDays: 14,
    moveOutNoticeDays: 14,
  },
  {
    code: "ID",
    name: "Idaho",
    slug: "idaho",
    statute: "Idaho Code §55-2305 et seq. — Self-Service Storage Facility Act",
    lateFeeCapCents: 2000,
    gracePeriodDays: 5,
    noticeOfDefaultDays: 30,
    lienSaleNoticeDays: 14,
    moveOutNoticeDays: 14,
  },
  {
    code: "IL",
    name: "Illinois",
    slug: "illinois",
    statute: "770 ILCS 95/4 — Self-Service Storage Facility Act",
    lateFeeCapCents: 2000,
    gracePeriodDays: 5,
    noticeOfDefaultDays: 14,
    lienSaleNoticeDays: 14,
    moveOutNoticeDays: 14,
  },
  {
    code: "IN",
    name: "Indiana",
    slug: "indiana",
    statute: "IC §26-3-8-1 et seq. — Self-Service Storage Facilities",
    lateFeeCapCents: 2000,
    gracePeriodDays: 5,
    noticeOfDefaultDays: 30,
    lienSaleNoticeDays: 30,
    moveOutNoticeDays: 14,
  },
  {
    code: "IA",
    name: "Iowa",
    slug: "iowa",
    statute: "Iowa Code Ch. 578A — Self-Service Storage Facility Liens",
    lateFeeCapCents: 2000,
    gracePeriodDays: 5,
    noticeOfDefaultDays: 30,
    lienSaleNoticeDays: 14,
    moveOutNoticeDays: 14,
  },
  {
    code: "KS",
    name: "Kansas",
    slug: "kansas",
    statute: "K.S.A. §58-815 et seq. — Self-Service Storage Act",
    lateFeeCapCents: 2000,
    gracePeriodDays: 5,
    noticeOfDefaultDays: 30,
    lienSaleNoticeDays: 10,
    moveOutNoticeDays: 14,
  },
  {
    code: "KY",
    name: "Kentucky",
    slug: "kentucky",
    statute: "KRS §359.200 et seq. — Self-Service Storage Facility Act",
    lateFeeCapCents: 2000,
    gracePeriodDays: 5,
    noticeOfDefaultDays: 30,
    lienSaleNoticeDays: 15,
    moveOutNoticeDays: 14,
  },
  {
    code: "LA",
    name: "Louisiana",
    slug: "louisiana",
    statute: "La. Rev. Stat. §9:4756 et seq. — Self-Service Storage Facility Act",
    lateFeeCapCents: 2000,
    gracePeriodDays: 5,
    noticeOfDefaultDays: 30,
    lienSaleNoticeDays: 14,
    moveOutNoticeDays: 14,
  },
  {
    code: "ME",
    name: "Maine",
    slug: "maine",
    statute: "10 M.R.S. §1371 et seq. — Self-Service Storage Facilities",
    lateFeeCapCents: 2000,
    gracePeriodDays: 5,
    noticeOfDefaultDays: 30,
    lienSaleNoticeDays: 14,
    moveOutNoticeDays: 14,
  },
  {
    code: "MD",
    name: "Maryland",
    slug: "maryland",
    statute: "Md. Comm. Law §18-501 et seq. — Self-Service Storage Facilities",
    lateFeeCapCents: 2000,
    gracePeriodDays: 5,
    noticeOfDefaultDays: 30,
    lienSaleNoticeDays: 14,
    moveOutNoticeDays: 14,
  },
  {
    code: "MA",
    name: "Massachusetts",
    slug: "massachusetts",
    statute: "Mass. Gen. Laws ch. 105A §§1-7 — Self-Service Storage Facilities",
    lateFeeCapCents: 2000,
    gracePeriodDays: 5,
    noticeOfDefaultDays: 45,
    lienSaleNoticeDays: 21,
    moveOutNoticeDays: 14,
  },
  {
    code: "MI",
    name: "Michigan",
    slug: "michigan",
    statute: "MCL §570.521 et seq. — Self-Service Storage Facility Act",
    lateFeeCapCents: 2000,
    gracePeriodDays: 5,
    noticeOfDefaultDays: 10,
    lienSaleNoticeDays: 14,
    moveOutNoticeDays: 14,
  },
  {
    code: "MN",
    name: "Minnesota",
    slug: "minnesota",
    statute: "Minn. Stat. §514.971 — Self-Service Storage Facility Act",
    lateFeeCapCents: 2000,
    gracePeriodDays: 5,
    noticeOfDefaultDays: 30,
    lienSaleNoticeDays: 14,
    moveOutNoticeDays: 14,
  },
  {
    code: "MS",
    name: "Mississippi",
    slug: "mississippi",
    statute: "Miss. Code §85-7-121 et seq. — Self-Service Storage Facilities",
    lateFeeCapCents: 2000,
    gracePeriodDays: 5,
    noticeOfDefaultDays: 30,
    lienSaleNoticeDays: 14,
    moveOutNoticeDays: 14,
  },
  {
    code: "MO",
    name: "Missouri",
    slug: "missouri",
    statute: "RSMo §415.400 et seq. — Self-Service Storage Facilities",
    lateFeeCapCents: 2000,
    gracePeriodDays: 5,
    noticeOfDefaultDays: 30,
    lienSaleNoticeDays: 15,
    moveOutNoticeDays: 14,
  },
  {
    code: "MT",
    name: "Montana",
    slug: "montana",
    statute: "MCA §70-6-401 et seq. — Self-Service Storage Facility Act",
    lateFeeCapCents: 2000,
    gracePeriodDays: 5,
    noticeOfDefaultDays: 30,
    lienSaleNoticeDays: 14,
    moveOutNoticeDays: 14,
  },
  {
    code: "NE",
    name: "Nebraska",
    slug: "nebraska",
    statute: "Neb. Rev. Stat. §69-2301 et seq. — Self-Service Storage Facility Act",
    lateFeeCapCents: 2000,
    gracePeriodDays: 5,
    noticeOfDefaultDays: 30,
    lienSaleNoticeDays: 14,
    moveOutNoticeDays: 14,
  },
  {
    code: "NV",
    name: "Nevada",
    slug: "nevada",
    statute: "NRS §108.470 et seq. — Self-Service Storage Facilities",
    lateFeeCapCents: 2000,
    gracePeriodDays: 5,
    noticeOfDefaultDays: 14,
    lienSaleNoticeDays: 14,
    moveOutNoticeDays: 14,
  },
  {
    code: "NH",
    name: "New Hampshire",
    slug: "new-hampshire",
    statute: "RSA §451-C — Self-Storage Facilities",
    lateFeeCapCents: 2000,
    gracePeriodDays: 5,
    noticeOfDefaultDays: 30,
    lienSaleNoticeDays: 14,
    moveOutNoticeDays: 14,
  },
  {
    code: "NJ",
    name: "New Jersey",
    slug: "new-jersey",
    statute: "N.J.S.A. §2A:44-187 et seq. — Self-Service Storage Facility Act",
    lateFeeCapCents: 2000,
    gracePeriodDays: 5,
    noticeOfDefaultDays: 30,
    lienSaleNoticeDays: 15,
    moveOutNoticeDays: 14,
  },
  {
    code: "NM",
    name: "New Mexico",
    slug: "new-mexico",
    statute: "NMSA §48-11-1 et seq. — Self-Service Storage Lien Act",
    lateFeeCapCents: 2000,
    gracePeriodDays: 5,
    noticeOfDefaultDays: 30,
    lienSaleNoticeDays: 14,
    moveOutNoticeDays: 14,
  },
  {
    code: "NY",
    name: "New York",
    slug: "new-york",
    statute: "N.Y. Lien Law §182 — Self-Service Storage Facilities",
    lateFeeCapCents: 2000,
    gracePeriodDays: 10,
    noticeOfDefaultDays: 60,
    lienSaleNoticeDays: 30,
    moveOutNoticeDays: 14,
  },
  {
    code: "NC",
    name: "North Carolina",
    slug: "north-carolina",
    statute: "N.C. Gen. Stat. §44A-40 et seq. — Self-Service Storage Liens",
    lateFeeCapCents: 2000,
    gracePeriodDays: 5,
    noticeOfDefaultDays: 15,
    lienSaleNoticeDays: 15,
    moveOutNoticeDays: 14,
  },
  {
    code: "ND",
    name: "North Dakota",
    slug: "north-dakota",
    statute: "N.D. Cent. Code §35-25.2 — Self-Service Storage Facility Act",
    lateFeeCapCents: 2000,
    gracePeriodDays: 5,
    noticeOfDefaultDays: 30,
    lienSaleNoticeDays: 15,
    moveOutNoticeDays: 14,
  },
  {
    code: "OH",
    name: "Ohio",
    slug: "ohio",
    statute: "ORC §5322.01 et seq. — Self-Service Storage Facility Act",
    lateFeeCapCents: 2000,
    gracePeriodDays: 5,
    noticeOfDefaultDays: 30,
    lienSaleNoticeDays: 30,
    moveOutNoticeDays: 14,
  },
  {
    code: "OK",
    name: "Oklahoma",
    slug: "oklahoma",
    statute: "42 O.S. §198.1 et seq. — Self-Service Storage Facility Liens",
    lateFeeCapCents: 2000,
    gracePeriodDays: 5,
    noticeOfDefaultDays: 30,
    lienSaleNoticeDays: 14,
    moveOutNoticeDays: 14,
  },
  {
    code: "OR",
    name: "Oregon",
    slug: "oregon",
    statute: "ORS §87.685 et seq. — Self-Service Storage Liens",
    lateFeeCapCents: 2000,
    gracePeriodDays: 7,
    noticeOfDefaultDays: 14,
    lienSaleNoticeDays: 14,
    moveOutNoticeDays: 14,
  },
  {
    code: "PA",
    name: "Pennsylvania",
    slug: "pennsylvania",
    statute: "73 P.S. §1901 et seq. — Pennsylvania Self-Service Storage Facility Act",
    lateFeeCapCents: 2000,
    gracePeriodDays: 5,
    noticeOfDefaultDays: 30,
    lienSaleNoticeDays: 30,
    moveOutNoticeDays: 14,
  },
  {
    code: "RI",
    name: "Rhode Island",
    slug: "rhode-island",
    statute: "R.I. Gen. Laws §34-42 — Self-Service Storage Facility Liens",
    lateFeeCapCents: 2000,
    gracePeriodDays: 5,
    noticeOfDefaultDays: 30,
    lienSaleNoticeDays: 15,
    moveOutNoticeDays: 14,
  },
  {
    code: "SC",
    name: "South Carolina",
    slug: "south-carolina",
    statute: "S.C. Code §39-20-10 et seq. — Self-Service Storage Facility Liens",
    lateFeeCapCents: 2000,
    gracePeriodDays: 5,
    noticeOfDefaultDays: 30,
    lienSaleNoticeDays: 15,
    moveOutNoticeDays: 14,
  },
  {
    code: "SD",
    name: "South Dakota",
    slug: "south-dakota",
    statute: "S.D. Codified Laws §44-12 — Self-Service Storage Facility Act",
    lateFeeCapCents: 2000,
    gracePeriodDays: 5,
    noticeOfDefaultDays: 30,
    lienSaleNoticeDays: 14,
    moveOutNoticeDays: 14,
  },
  {
    code: "TN",
    name: "Tennessee",
    slug: "tennessee",
    statute: "T.C.A. §66-31-101 et seq. — Self-Service Storage Facility Act",
    lateFeeCapCents: 2000,
    gracePeriodDays: 5,
    noticeOfDefaultDays: 30,
    lienSaleNoticeDays: 15,
    moveOutNoticeDays: 14,
  },
  {
    code: "TX",
    name: "Texas",
    slug: "texas",
    statute: "Tex. Prop. Code §59.001 — Self-Service Storage Facility Liens",
    lateFeeCapCents: 2000,
    gracePeriodDays: 5,
    noticeOfDefaultDays: 45,
    lienSaleNoticeDays: 14,
    moveOutNoticeDays: 14,
  },
  {
    code: "UT",
    name: "Utah",
    slug: "utah",
    statute: "Utah Code §38-8-1 et seq. — Self-Service Storage Facilities Lien Act",
    lateFeeCapCents: 2000,
    gracePeriodDays: 5,
    noticeOfDefaultDays: 30,
    lienSaleNoticeDays: 15,
    moveOutNoticeDays: 14,
  },
  {
    code: "VT",
    name: "Vermont",
    slug: "vermont",
    statute: "9 V.S.A. §3901 et seq. — Self-Service Storage Liens",
    lateFeeCapCents: 2000,
    gracePeriodDays: 5,
    noticeOfDefaultDays: 30,
    lienSaleNoticeDays: 14,
    moveOutNoticeDays: 14,
  },
  {
    code: "VA",
    name: "Virginia",
    slug: "virginia",
    statute: "Va. Code §55.1-2900 et seq. — Self-Service Storage Act",
    lateFeeCapCents: 2000,
    gracePeriodDays: 5,
    noticeOfDefaultDays: 10,
    lienSaleNoticeDays: 10,
    moveOutNoticeDays: 14,
  },
  {
    code: "WA",
    name: "Washington",
    slug: "washington",
    statute: "RCW 19.150 — Self-Service Storage Facilities",
    lateFeeCapCents: 2000,
    gracePeriodDays: 14,
    noticeOfDefaultDays: 14,
    lienSaleNoticeDays: 14,
    moveOutNoticeDays: 14,
  },
  {
    code: "WV",
    name: "West Virginia",
    slug: "west-virginia",
    statute: "W. Va. Code §38-14-1 et seq. — Self-Service Storage Liens",
    lateFeeCapCents: 2000,
    gracePeriodDays: 5,
    noticeOfDefaultDays: 30,
    lienSaleNoticeDays: 15,
    moveOutNoticeDays: 14,
  },
  {
    code: "WI",
    name: "Wisconsin",
    slug: "wisconsin",
    statute: "Wis. Stat. §704.90 — Self-Service Storage Facilities",
    lateFeeCapCents: 2000,
    gracePeriodDays: 5,
    noticeOfDefaultDays: 20,
    lienSaleNoticeDays: 20,
    moveOutNoticeDays: 14,
  },
  {
    code: "WY",
    name: "Wyoming",
    slug: "wyoming",
    statute: "Wyo. Stat. §29-7-101 et seq. — Self-Service Storage Facilities",
    lateFeeCapCents: 2000,
    gracePeriodDays: 5,
    noticeOfDefaultDays: 30,
    lienSaleNoticeDays: 14,
    moveOutNoticeDays: 14,
  },
];

export const STATE_BY_SLUG: Record<string, StateRuleMarketing> =
  Object.fromEntries(STATE_RULES_MARKETING.map((s) => [s.slug, s]));
